import { Calendar } from "@/components/ui/calendar";
import { useTranslations } from "use-intl";
import { useFormatPickedDate } from "@/i18n/useFormatPickedDate";
import ActualSekAmountField from "./ActualSekAmountField";

interface ExternalSettlementFieldsProps {
  /** Rendered above the calendar when given; omitted entirely for a case-c acknowledgment view
   * that already states its own context (payables' case c has none; receivables' has one). */
  dateLabel?: string;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  /** AC9 (payables) / FIN-1023 AC-3 (receivables) — a non-SEK document has no bank transaction
   * to derive the real settled amount from on this path, so the consultant types it. */
  showSekAmountField: boolean;
  actualSekAmountInput: string;
  onActualSekAmountInputChange: (value: string) => void;
  /** Unique per call site — two of these can be mounted in the same page (e.g. two open rows'
   * modals are never simultaneous today, but ids must still not collide across document types). */
  sekAmountFieldId: string;
}

/**
 * Shared between PayableMatchSuggestionsModal and receivables' MarkAsReceivedModal (previously
 * each component's own renderPaymentDetailsFields/renderDatePicker, byte-identical once both
 * supported a foreign-currency amount field): the trusted date-picker, plus the required SEK
 * amount input when the document is in a foreign currency.
 */
export default function ExternalSettlementFields({
  dateLabel,
  selectedDate,
  onSelectDate,
  showSekAmountField,
  actualSekAmountInput,
  onActualSekAmountInputChange,
  sekAmountFieldId,
}: ExternalSettlementFieldsProps) {
  const t = useTranslations("consultantMatching.shared");
  const formatPickedDate = useFormatPickedDate();
  return (
    <>
      {dateLabel && <div className="text-sm text-muted-foreground">{dateLabel}</div>}
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(date) => date && onSelectDate(date)}
          autoFocus
          className="rounded-md border shadow p-3 pointer-events-auto"
        />
      </div>
      <div className="text-sm text-center">
        {t("selectedDate")} <span className="font-medium">{formatPickedDate(selectedDate)}</span>
      </div>
      {showSekAmountField && (
        <ActualSekAmountField
          id={sekAmountFieldId}
          value={actualSekAmountInput}
          onChange={onActualSekAmountInputChange}
        />
      )}
    </>
  );
}
